"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LinkSecureScaleStatus = exports.LinkCrmStatusView = exports.LinkCrmStatus = exports.LinkCrmReceiptQueue = exports.LINK_CRM_MAX_TILLS = exports.LinkCrmErrorKey = exports.LinkCrmOutcome = exports.LINK_CRM_OUTCOMES = exports.LinkCrmState = exports.LinkExportRetryOutcome = exports.LinkExportProblem = exports.LinkExportQueueCounts = exports.LinkSourceStatus = exports.LinkSourceImportState = exports.LinkAdvertisedUrl = exports.LinkCertificateFingerprint = exports.LinkHealth = exports.LINK_STATUS_MAX_RETRY_REQUESTS = exports.LINK_STATUS_MAX_PROBLEMS = exports.LINK_STATUS_TEXT_MAX = exports.LINK_STATUS_SCHEMA_VERSION = exports.LinkDevicesResponse = exports.LinkDeviceSummary = exports.LinkDeviceSourceSummary = exports.LinkConfigResponse = exports.LinkConfigExportCashier = exports.LinkConfigRegister = exports.LinkCatalogueTaxGroup = exports.LinkPinPepper = exports.LinkConnectorSource = exports.LinkIp4ConnectorSource = exports.LINK_FEED_LOTTERY_QUEUE_DEFAULT = exports.LinkIp4Queue = exports.LINK_FEED_QUEUE_KEYS = exports.LinkSanitexNavConnectorSource = exports.LinkSanitexTenderMethod = exports.LinkIp3ConnectorSource = exports.LinkIp3CertificateTypeCode = exports.LINK_IP3_RESERVED_PAYMENT_TYPE_CODES = exports.LINK_IP3_CERTIFICATE_TYPE_CODE_PATTERN = exports.LinkIp3PhoneFormat = exports.LINK_IP3_TIMEOUT_DEFAULT_MS = exports.LINK_IP3_TIMEOUT_MAX_MS = exports.LINK_IP3_TIMEOUT_MIN_MS = exports.LinkIp2ConnectorSource = exports.LinkIp1ConnectorSource = exports.LinkSourceCredentials = exports.LinkConfigLocation = exports.LinkConnectorProfile = exports.LINK_CONFIG_SCHEMA_VERSION = void 0;
exports.LinkStatusView = exports.LinkCardProvenanceSupportView = exports.LinkExportRetryRequestView = exports.LINK_STATUS_STALE_AFTER_MS = exports.LinkReportState = exports.LinkStatusResponse = exports.LinkExportRetryRequest = exports.LinkStatusReport = exports.LinkDocumentFeedStatus = exports.LinkDocumentFeedProblem = exports.LinkDocumentFeedQueueStatus = exports.LinkDocumentFeedConnectionState = void 0;
const storage_pressure_js_1 = require("./storage-pressure.js");
const zod_1 = require("zod");
const link_certificate_js_1 = require("./link-certificate.js");
const link_card_provenance_js_1 = require("./link-card-provenance.js");
const link_gift_certificates_js_1 = require("./link-gift-certificates.js");
const link_relay_js_1 = require("./link-relay.js");
const link_crm_receipt_actions_js_1 = require("./link-crm-receipt-actions.js");
const store_pass_js_1 = require("./store-pass.js");
const store_link_js_1 = require("./store-link.js");
const varus_catalogue_js_1 = require("./varus-catalogue.js");
const link_export_correction_js_1 = require("./link-export-correction.js");
/**
 * Back Office → re:form.lite.link device surface (RL-449, ADR-0014 §2).
 *
 * `GET /api/v1/link/config` is what an activated Link pulls, with its own
 * terminal token, to learn what it serves: its store and that store's connector
 * sources. Configuration stays authored in Back Office; Link pulls it whenever
 * it is online, the way tills pull terminal params.
 *
 * ------------------------------------------------------------------
 * HOW THIS GROWS WITHOUT BREAKING
 * ------------------------------------------------------------------
 * Later tickets add whole sections rather than change existing ones:
 *
 *   `storePassKeys`  RL-450 — Ed25519 public keys (current + previous)
 *   `pinPepper`      RL-451 — `pinKeyVersion` + pepper material (delivered)
 *   `settings`       RL-452 — the `connector_location_settings` row
 *                    (delivered from RL-459)
 *   `catalogueTaxGroups` RL-459 — the ip-1 tax-group mapping a published
 *                    catalogue validates against and leads with
 *
 * Each arrives as an OPTIONAL top-level key, so a Link built before it parses
 * the response unchanged (zod objects strip unknown keys; the Link must do the
 * same) and a Link built after it treats "absent" as "this Back Office does not
 * deliver it yet". Nothing here is ever renamed or narrowed in place; a breaking
 * shape gets a new `schemaVersion`.
 *
 * ------------------------------------------------------------------
 * CREDENTIALS
 * ------------------------------------------------------------------
 * This is the ONLY response in Back Office that carries a connector source's
 * store-system passwords in the clear. It goes only to the Link of the same
 * location, over its authenticated channel; the operator list and detail APIs
 * never return them. A Link must store them encrypted at rest and must never
 * log this body.
 */
exports.LINK_CONFIG_SCHEMA_VERSION = 1;
/** Connector profile ids — `docs/integration/profiles.md`. */
exports.LinkConnectorProfile = zod_1.z.enum(['ip-1', 'ip-2', 'ip-3', 'ip-4', 'sanitex-nav']);
exports.LinkConfigLocation = zod_1.z.object({
    id: zod_1.z.string(),
    code: zod_1.z.string(),
    name: zod_1.z.string(),
    countryCode: zod_1.z.string().nullable(),
    timezone: zod_1.z.string().nullable(),
});
/** A database login, decrypted for the Link that will use it. */
exports.LinkSourceCredentials = zod_1.z.object({
    username: zod_1.z.string(),
    password: zod_1.z.string(),
});
/**
 * One `ip-1` connector source of the Link's store — the same record the
 * operator authors under Settings → Integrations, with its credentials.
 *
 * Grouped as connection / import / export so a second profile (`ip-2`, RL-473)
 * reuses the envelope with its own fields rather than widening a flat bag.
 */
exports.LinkIp1ConnectorSource = zod_1.z.object({
    id: zod_1.z.string(),
    profile: zod_1.z.literal('ip-1'),
    name: zod_1.z.string(),
    /** A disabled source is still sent, so Link stops it rather than forgetting it. */
    enabled: zod_1.z.boolean(),
    /** Changes whenever the operator edits the source; lets Link skip unchanged ones. */
    updatedAt: zod_1.z.string().datetime(),
    connection: zod_1.z.object({
        host: zod_1.z.string(),
        port: zod_1.z.number().int(),
        databaseName: zod_1.z.string(),
        /** The store's id inside a database that serves several stores; null = one store. */
        shopId: zod_1.z.string().nullable(),
        ssl: zod_1.z.boolean(),
        statementTimeoutMs: zod_1.z.number().int(),
        /** The read-only login the import uses. */
        credentials: exports.LinkSourceCredentials,
    }),
    import: zod_1.z.object({
        batchSize: zod_1.z.number().int(),
        pollIntervalSeconds: zod_1.z.number().int(),
        currencyCode: zod_1.z.string(),
        ageAssetKey: zod_1.z.string().nullable(),
        restrictedAgeYears: zod_1.z.number().int(),
        ageRestrictedTaxGroups: zod_1.z.array(zod_1.z.number().int()),
    }),
    export: zod_1.z.object({
        enabled: zod_1.z.boolean(),
        zReports: zod_1.z.boolean(),
        /**
         * VL-521: cancelled receipts (type 4) are written too. Absent — a Back
         * Office from before it — is off, as before.
         */
        cancelledReceipts: zod_1.z.boolean().optional(),
        from: zod_1.z.string().datetime().nullable(),
        cashInReceiptType: zod_1.z.number().int(),
        cashOutReceiptType: zod_1.z.number().int(),
        /** The separate write login; null until an operator sets one. */
        credentials: exports.LinkSourceCredentials.nullable(),
    }),
});
/**
 * One `ip-2` connector source (RL-473) — GP master data, read-only, run only
 * on Link. The same envelope as `ip-1`, with that profile's fields: what was
 * the retired sidecar's cleartext `appsettings.json` on the till.
 *
 * Maps one to one onto `Ip2Config` in `@reform-lite/connectors`
 * (`databaseName` → `database`, `credentials` → `user` / `password`), and
 * Back Office validated it with that same parser before storing it. No
 * `export` section: the profile writes nothing back.
 */
exports.LinkIp2ConnectorSource = zod_1.z.object({
    id: zod_1.z.string(),
    profile: zod_1.z.literal('ip-2'),
    name: zod_1.z.string(),
    /** A disabled source is still sent, so Link stops it rather than forgetting it. */
    enabled: zod_1.z.boolean(),
    /** Changes whenever the operator edits the source; lets Link skip unchanged ones. */
    updatedAt: zod_1.z.string().datetime(),
    connection: zod_1.z.object({
        /** SQL Server host; `host,port` and `host\instance` as in a SqlClient string. */
        server: zod_1.z.string(),
        databaseName: zod_1.z.string(),
        connectTimeoutSeconds: zod_1.z.number().int(),
        commandTimeoutSeconds: zod_1.z.number().int(),
        /** The read-only GP login. */
        credentials: exports.LinkSourceCredentials,
    }),
    import: zod_1.z.object({
        hierarchyCatalogCode: zod_1.z.string(),
        departmentCode: zod_1.z.string(),
        languageId: zod_1.z.number().int(),
        /** ISO currency for a price row whose own currency is blank. */
        currency: zod_1.z.string(),
        /** GP unit code → unit of measure; empty makes every item `each`. */
        unitMap: zod_1.z.record(zod_1.z.string(), zod_1.z.string()),
    }),
});
/** RL-569: bounds of an `ip-3` source's per-request CRM timeout. */
exports.LINK_IP3_TIMEOUT_MIN_MS = 1_000;
/**
 * The ceiling is below the till's own wait for a Link loyalty answer
 * (`LINK_LOYALTY_TILL_TIMEOUT_MS`), so the Link always answers — with the
 * CRM's result or with "unavailable" — before the till gives up on it.
 */
exports.LINK_IP3_TIMEOUT_MAX_MS = 20_000;
exports.LINK_IP3_TIMEOUT_DEFAULT_MS = 15_000;
/**
 * How the CRM wants a phone number written: `e164_plus` `+380…`, or
 * `digits_only` `380…` (some processing APIs refuse the `+`).
 */
exports.LinkIp3PhoneFormat = zod_1.z.enum(['e164_plus', 'digits_only']);
/**
 * The CRM's base URL: `https`, or `http` only with `allowInsecureHttp`; never
 * user info (the login travels as Basic auth from `credentials`, and a URL is
 * the kind of value that ends up in logs and error messages).
 */
const LinkIp3Connection = zod_1.z
    .object({
    baseUrl: zod_1.z.string().max(512).url(),
    /** Default false. Back Office warns and audits when an operator sets it. */
    allowInsecureHttp: zod_1.z.boolean(),
    /** Per CRM request, the response body included. */
    timeoutMs: zod_1.z.number().int().min(exports.LINK_IP3_TIMEOUT_MIN_MS).max(exports.LINK_IP3_TIMEOUT_MAX_MS),
    /** The CRM service account (Basic auth). Decrypted only in this response. */
    credentials: exports.LinkSourceCredentials,
})
    .superRefine((connection, ctx) => {
    let url;
    try {
        url = new URL(connection.baseUrl);
    }
    catch {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['baseUrl'], message: 'not a URL' });
        return;
    }
    if (url.username !== '' || url.password !== '') {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['baseUrl'],
            message: 'must not carry user info',
        });
    }
    const secure = url.protocol === 'https:';
    const insecureAllowed = url.protocol === 'http:' && connection.allowInsecureHttp;
    if (!secure && !insecureAllowed) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['baseUrl'],
            message: 'must be https (http only with allowInsecureHttp)',
        });
    }
});
/**
 * VL-379: the CRM's `Payments.TypeCode` for a gift certificate in "set
 * online". A short code, never `0` (card) or `1` (cash) — the codes the Link
 * already sends for those legs, under which a certificate would be booked as
 * another tender.
 *
 * RETIRED (owner decision 2026-10-22, VL-379 Q1): certificates go as
 * `ExternalGiftCards`, never in `Payments`. Still accepted and checked so a
 * stored value keeps parsing; no Link reads it. To be removed (with the
 * `creatioTypeCode` key) once no source carries it.
 */
exports.LINK_IP3_CERTIFICATE_TYPE_CODE_PATTERN = /^[0-9A-Za-z_.-]{1,32}$/;
exports.LINK_IP3_RESERVED_PAYMENT_TYPE_CODES = ['0', '1'];
exports.LinkIp3CertificateTypeCode = zod_1.z
    .string()
    .regex(exports.LINK_IP3_CERTIFICATE_TYPE_CODE_PATTERN)
    .refine((code) => !exports.LINK_IP3_RESERVED_PAYMENT_TYPE_CODES.includes(code), {
    message: 'must not be the cash or card payment type code',
});
/**
 * One `ip-3` connector source (RL-569) — "CRM loyalty": the store's loyalty
 * processing (a CRM with a card / phone balance API) that can be reached only
 * from inside the retailer's network, so only the store's Link talks to it.
 * Link-only, like `ip-2`: Back Office authors and stores it, never calls it.
 *
 * - The register a CRM call is made for is not in the source: Link takes it
 *   from `registers[]` by the store pass's `sub`, so a till cannot claim
 *   another till's register.
 * - `crm.operations` switches each operation on separately; a disabled one
 *   answers "unavailable" to the till.
 *
 * Maps onto the `ip-3` configuration parser in `@reform-lite/connectors`, and
 * Back Office validates a source with that parser before storing it.
 */
exports.LinkIp3ConnectorSource = zod_1.z.object({
    id: zod_1.z.string(),
    profile: zod_1.z.literal('ip-3'),
    name: zod_1.z.string(),
    /** A disabled source is still sent, so Link stops it rather than forgetting it. */
    enabled: zod_1.z.boolean(),
    /** Changes whenever the operator edits the source; lets Link skip unchanged ones. */
    updatedAt: zod_1.z.string().datetime(),
    connection: LinkIp3Connection,
    crm: zod_1.z.object({
        /** The store's code in the CRM. */
        tradePointCode: zod_1.z.string().min(1).max(64),
        phoneFormat: exports.LinkIp3PhoneFormat,
        operations: zod_1.z.object({
            cardLookup: zod_1.z.boolean(),
            phoneLookup: zod_1.z.boolean(),
            receiptPreference: zod_1.z.boolean(),
            /**
             * RL-587: send the store's carded receipts to the CRM, which awards
             * bonuses from them. Optional: a Back Office that predates it sends
             * none, and absent is OFF — a Link never starts writing purchases into
             * a CRM nobody asked it to.
             */
            receiptSubmission: zod_1.z.boolean().optional(),
            /**
             * VL-103: a receipt whose CRM call did not go through is handed to the
             * store's own offline processing (one row with the initial action code
             * in its offline-processing table, through the store's `ip-1` export
             * login) instead of being retried by the Link. Optional, absent is OFF;
             * it takes effect only with `receiptSubmission` on.
             */
            offlineProcessing: zod_1.z.boolean().optional(),
        }),
        /**
         * VL-379: how a gift certificate paid on a receipt goes into "set
         * online". Optional, and absent is today's behaviour: no certificate data
         * at all.
         *
         * - `externalGiftCards` (owner decision 2026-10-22, Q1): `true` sends each
         *   certificate as the store's current till does, one `ExternalGiftCards`
         *   entry `{ Number, Amount }` (the charge, minor units), and nothing for
         *   it in `Payments`. Absent or `false` is off. A Link built before this
         *   key refuses a block without `creatioTypeCode`, so switch it on only
         *   once the store's Link runs a build that knows it.
         * - `creatioTypeCode`: retired, see [LinkIp3CertificateTypeCode]. Parsed,
         *   ignored.
         *
         * A block must carry at least one of the two.
         */
        certificates: zod_1.z
            .object({
            creatioTypeCode: exports.LinkIp3CertificateTypeCode.optional(),
            externalGiftCards: zod_1.z.boolean().optional(),
        })
            .refine((block) => block.creatioTypeCode !== undefined || block.externalGiftCards !== undefined, {
            message: 'must carry externalGiftCards or creatioTypeCode',
        })
            .optional(),
        /**
         * VL-102 LAB ONLY: `labEnabled: true` lets the Link admit the two-step
         * CRM purchase path (the VL-104/VL-105 landing containment) for THIS
         * source only, for a supervised lab test. Not a production enablement
         * contract. Optional; absent or `false` is today's behaviour, the
         * containment. Back Office sets it only through the admin API with an
         * explicit `labEnabledAcknowledged: "LAB ONLY"` and audits the change.
         *
         * A Link built before this key strips the block (this object is not
         * strict) and keeps the containment: the source still runs, the switch
         * does nothing there.
         */
        purchases: zod_1.z
            .object({
            labEnabled: zod_1.z.boolean(),
        })
            .optional(),
    }),
});
/**
 * Any connector source, told apart by `profile`. A Link must treat a source
 * whose profile it does not implement as "not mine to run" and report it,
 * never fail the whole pull over it: parse `connectorSources` entry by entry.
 *
 * RL-569 added `ip-3`. A Link built before it skips an `ip-3` source as an
 * unknown profile and keeps running the others (`backoffice-client.ts`,
 * `linkConfig`; proven in `tests/link-ip3.test.ts`).
 */
/**
 * The till tender methods a store's NAV `$Tender Type` code may be authored
 * against (SNX-PAY-16 / SNX-PAY-17). One list for the contract and the Link
 * projector, so a method the till records can never be accepted by one and
 * refused by the other.
 *
 * `credit` is the Sanitex credit sale (Kredits, NAV tender `4` in the agreed
 * table), recorded under that id by the till. `onAccount` is kept for
 * configurations already authored against it; no till tender records it.
 */
exports.LinkSanitexTenderMethod = zod_1.z.enum([
    'cash',
    'card',
    'credit',
    'onAccount',
    'loyalty',
    'voucher',
    'taromatas',
]);
/** Sanitex NAV is opt-in; credentials leave BOS only for the store's Link. */
exports.LinkSanitexNavConnectorSource = zod_1.z.object({
    id: zod_1.z.string().min(1),
    profile: zod_1.z.literal('sanitex-nav'),
    name: zod_1.z.string().min(1).max(128),
    enabled: zod_1.z.boolean(),
    updatedAt: zod_1.z.string().datetime(),
    connection: zod_1.z.object({
        host: zod_1.z
            .string()
            .min(1)
            .max(255)
            .regex(/^[a-zA-Z0-9.:-]+$/),
        port: zod_1.z.number().int().min(1).max(65535),
        databaseName: zod_1.z.string().min(1).max(128),
        credentials: exports.LinkSourceCredentials,
        encrypt: zod_1.z.boolean(),
        trustServerCertificate: zod_1.z.boolean(),
        statementTimeoutMs: zod_1.z.number().int().min(1000).max(120000),
    }),
    nav: zod_1.z.object({
        masterRefreshSeconds: zod_1.z.number().int().min(60).max(86400).default(300),
        classFamilies: zod_1.z
            .record(zod_1.z.enum(['alcohol', 'tobacco', 'tobaccoRelated', 'deposit', 'produce', 'firewood', 'none']))
            .default({}),
        licenceSubtypeTypes: zod_1.z
            .object({
            alcohol: zod_1.z.number().int().positive(),
            tobacco: zod_1.z.number().int().positive(),
            tobaccoRelated: zod_1.z.number().int().positive(),
        })
            .optional(),
        /** SNX-1441: meaning of each NAV `Barcode Mask Character.[Character Type]`
         * option value for the till's mask decoder. Absent: the barcode-mask
         * tables are not read and no mask feed is published. */
        barcodeMaskTypes: zod_1.z
            .record(zod_1.z.string().regex(/^\d{1,3}$/), zod_1.z
            .object({
            kind: zod_1.z.enum(['item', 'value', 'check', 'ignore']),
            valueSemantics: zod_1.z.enum(['weightGrams', 'priceMinor', 'count']).optional(),
        })
            .strict()
            .refine((t) => (t.kind === 'value') === (t.valueSemantics !== undefined)))
            .optional(),
        companyPrefix: zod_1.z
            .string()
            .min(1)
            .max(128)
            // NAV table names are `<company>$<table>`: no brackets, no control characters.
            // eslint-disable-next-line no-control-regex
            .regex(/^[^[\]\u0000-\u001f]+\$$/),
        storeCode: zod_1.z.string().min(1).max(32),
        /** Explicit exchange stock DB when NAVSTOCK lives outside the NAV company. */
        stockDatabaseName: zod_1.z.string().min(1).max(128).optional(),
        exchangeDatabaseName: zod_1.z.string().trim().min(1).max(128).optional(),
        /** Private Link-only service endpoint; authentication is explicitly authored. */
        getPrices: zod_1.z
            .object({
            endpoint: zod_1.z
                .string()
                .url()
                .max(2048)
                .refine((v) => {
                const u = new URL(v);
                return u.protocol === 'https:' && !u.username && !u.password && !u.hash;
            }),
            authentication: zod_1.z.enum(['none', 'sourceBasic']),
            timeoutMs: zod_1.z.number().int().min(1000).max(20000).default(10000),
        })
            .strict()
            .optional(),
        stockFirmId: zod_1.z.string().trim().min(1).max(64).optional(),
        tenderMethodCodes: zod_1.z.record(exports.LinkSanitexTenderMethod).default({}),
    }),
});
/**
 * VL-353 — integration profile `ip-4`, "Document feed (message queue)": the
 * store's Link publishes each till document (sale, cancellation, return, cash
 * in / out, Z report) to a message broker the store operates. Publish only —
 * the Link never declares an exchange or a queue.
 *
 * The queue keys are the closed set below. Each has its own switch, its own
 * exchange and routing key, and its own optional start number.
 *
 * VL-375 added `lotterySales`: one message per State Lottery ticket type sold
 * on a receipt (queue `all_checks_lottery`). A source stored before it has no
 * such key; it reads as that queue switched OFF with the default routing
 * below, so nothing new is published until an operator switches it on.
 */
exports.LINK_FEED_QUEUE_KEYS = [
    'sales',
    'cancellations',
    'returns',
    'cashMovements',
    'zReports',
    'lotterySales',
];
exports.LinkIp4Queue = zod_1.z
    .object({
    /** Off means paused, not skipped: documents received meanwhile are sent on enable. */
    enabled: zod_1.z.boolean(),
    exchange: zod_1.z.string().trim().min(1).max(255),
    /** May be empty (a fanout exchange ignores it). */
    routingKey: zod_1.z.string().max(255),
    /**
     * The first register document number to publish, compared within each
     * register's own numbering. A number — 0 included — reaches back: every
     * numbered document the Link holds from it up is published, whenever it
     * was received. Only null means "what is dated from the moment the queue
     * was first enabled".
     */
    startFromNumber: zod_1.z.number().int().min(0).nullable(),
})
    .strict();
/**
 * VL-375 — the `lotterySales` queue of a source that does not name it: off,
 * with the routing the store has not confirmed yet (the ticket leaves the
 * exchange and routing key of `all_checks_lottery` open). Switching it on
 * is an operator's act.
 */
exports.LINK_FEED_LOTTERY_QUEUE_DEFAULT = {
    enabled: false,
    exchange: 'all_checks_lottery_exchange',
    routingKey: 'all_checks_lottery',
    startFromNumber: null,
};
exports.LinkIp4ConnectorSource = zod_1.z.object({
    id: zod_1.z.string(),
    profile: zod_1.z.literal('ip-4'),
    name: zod_1.z.string(),
    /** The master switch; off pauses every queue. */
    enabled: zod_1.z.boolean(),
    updatedAt: zod_1.z.string().datetime(),
    connection: zod_1.z.object({
        protocol: zod_1.z.enum(['amqp', 'amqps']),
        host: zod_1.z.string().trim().min(1).max(255),
        port: zod_1.z.number().int().min(1).max(65535),
        /** May be empty; the broker's default virtual host is `/`. */
        vhost: zod_1.z.string().max(255),
        heartbeatSeconds: zod_1.z.number().int().min(5).max(600).default(30),
        connectTimeoutMs: zod_1.z.number().int().min(1000).max(60000).default(10000),
        credentials: exports.LinkSourceCredentials,
    }),
    feed: zod_1.z.object({
        /** The store's number in the consumer's system; sent as a number. */
        shopId: zod_1.z.string().regex(/^[0-9]{1,9}$/),
        taxNumber: zod_1.z.string().trim().max(32).nullable(),
        /** Receipt header lines; `{register}` is replaced by the register number. */
        headerLines: zod_1.z.array(zod_1.z.string().max(96)).max(10),
        footerLines: zod_1.z.array(zod_1.z.string().max(96)).max(4),
        queues: zod_1.z
            .object({
            sales: exports.LinkIp4Queue,
            cancellations: exports.LinkIp4Queue,
            returns: exports.LinkIp4Queue,
            cashMovements: exports.LinkIp4Queue,
            zReports: exports.LinkIp4Queue,
            lotterySales: exports.LinkIp4Queue.default(() => ({ ...exports.LINK_FEED_LOTTERY_QUEUE_DEFAULT })),
        })
            .strict(),
    }),
});
exports.LinkConnectorSource = zod_1.z.discriminatedUnion('profile', [
    exports.LinkIp1ConnectorSource,
    exports.LinkIp2ConnectorSource,
    exports.LinkIp3ConnectorSource,
    exports.LinkIp4ConnectorSource,
    exports.LinkSanitexNavConnectorSource,
]);
/**
 * The tenant PIN pepper for Link (RL-451, ADR-0014 §4): Link hashes the store
 * system's plain cashier PINs the moment it reads them, with
 * `hashCashierPin(pin, pepper, pinKeyVersion)` from `@reform-lite/connectors`.
 *
 * - `pinKeyVersion` is the tenant's CURRENT version. Link hashes every new PIN
 *   at it and treats a cashier it hashed at another version as needing a
 *   rewrite, so a rotation reaches Link on its next pull with nothing else to
 *   trigger. Older versions are not sent: Link writes hashes, it never needs
 *   to verify one the tills already hold.
 * - `material` is base64 of the pepper string's UTF-8 bytes, the same encoding
 *   as a terminal's `pinPeppers[].material`; decode it with
 *   `pinPepperFromMaterial`. It is `null` exactly when `pinKeyVersion` is 0:
 *   version 0 is the legacy DEPLOYMENT-wide pepper, shared by every tenant
 *   still on it, and is never delivered to a store server. A Link that
 *   receives `null` must not import cashier PINs until the tenant's PIN key
 *   has been rotated.
 *
 * Tenant-wide, not per store — ADR-0014 records that risk. Link keeps it with
 * the other secrets of this body and never logs it.
 */
exports.LinkPinPepper = zod_1.z
    .object({
    pinKeyVersion: zod_1.z.number().int().nonnegative(),
    material: zod_1.z.string().min(1).nullable(),
})
    .refine((value) => (value.pinKeyVersion === 0) === (value.material === null), {
    message: 'material is null exactly when pinKeyVersion is 0',
});
/**
 * One ip-1 tax-group → tax-category mapping, exactly the `tax-group` record a
 * published catalogue leads with (`VarusCatalogueRecord`, kind `tax-group`).
 *
 * Link publishes a store's catalogue itself when its `catalogueSource` is
 * `link` (RL-459), and publication refuses a catalogue with an unmapped tax
 * group. The mapping is authored in Back Office (Country packs → Tax category
 * mappings, source system `ip-1`), so Link receives it here and stores it with
 * each publication, as Back Office does.
 */
exports.LinkCatalogueTaxGroup = zod_1.z.object({
    kind: zod_1.z.literal('tax-group'),
    id: zod_1.z.string(),
    payload: varus_catalogue_js_1.VarusCatalogueTaxGroup,
});
/** RL-458: one till of the Link's store and its store-system register number. */
exports.LinkConfigRegister = zod_1.z.object({
    terminalId: zod_1.z.string().min(1),
    registerNumber: zod_1.z.string().min(1),
    /**
     * VL-183 — the till's kind. A self-checkout (`sco`) writes no Masterkasa
     * `metrics` row. Absent from an older Back Office: a staffed till, as before.
     */
    kind: zod_1.z.enum(['pos', 'sco', 'attendant']).optional(),
});
/**
 * RL-460: a cashier Back Office imported from one of the store's `ip-1`
 * sources, with the store-system identity the export writes documents under.
 * Receipts and shift events name the cashier by the id the till was served —
 * Back Office's, while tills take cashiers from Back Office — so a Link that
 * exports needs this to put a store cashier id on them. Not a secret: no PIN,
 * no hash.
 */
exports.LinkConfigExportCashier = zod_1.z.object({
    cashierId: zod_1.z.string().min(1),
    /** `t_kassirs.kassirid`. */
    kassirId: zod_1.z.string().min(1),
    /** `t_kassirs.login`; the Z report names the cashier by it. */
    login: zod_1.z.string().nullable(),
});
exports.LinkConfigResponse = zod_1.z.object({
    schemaVersion: zod_1.z.literal(exports.LINK_CONFIG_SCHEMA_VERSION),
    /** Tenant id — the `iss` a store pass must carry (RL-450). */
    tenantId: zod_1.z.string(),
    terminal: zod_1.z.object({ id: zod_1.z.string(), code: zod_1.z.string(), displayName: zod_1.z.string() }),
    location: exports.LinkConfigLocation,
    connectorSources: zod_1.z.array(exports.LinkConnectorSource),
    /** RL-451. Absent from a Back Office that does not deliver it yet. */
    pinPepper: exports.LinkPinPepper.optional(),
    /**
     * RL-450 — the keys a store pass may be signed with: the current key first,
     * then every retired key that may still have signed a pass that has not
     * expired. Link verifies by the pass's `kid` against this list and refuses a
     * kid it does not hold. Public halves only.
     */
    storePassKeys: zod_1.z.array(store_pass_js_1.StorePassPublicKey).optional(),
    /**
     * RL-459 — the ip-1 tax mapping, in tax-group order. Absent reads as none:
     * a Link then imports but cannot publish a catalogue that names a tax group.
     */
    catalogueTaxGroups: zod_1.z.array(exports.LinkCatalogueTaxGroup).optional(),
    /**
     * RL-458 — the store-system register number of each till of the Link's
     * store that has one (`Terminal.posExternalNumber`). The store catalogue
     * serves a lane its own prohibited barcodes (VL-290), keyed by this number,
     * and a store pass does not carry it; Link looks the pass's `sub` up here
     * so it serves the page Back Office would. Absent or unlisted: the store's
     * whole prohibition set, which is what Back Office serves a till without a
     * number. Not a secret.
     */
    registers: zod_1.z.array(exports.LinkConfigRegister).optional(),
    /**
     * RL-460 — the store's `connector_location_settings` row and every period
     * of export ownership: which documents this Link exports (those created in
     * a `link` period, `exportOwnerAt`), including after the owner moved away.
     * Absent from an older Back Office: the Link then exports nothing, since it
     * was never told it owns anything — and (RL-459) imports nothing either:
     * it imports the store's catalogue only while `catalogueSource` is `link`
     * (ADR-0014 §5: exactly one owner), and absent reads as the default,
     * `backoffice`.
     */
    settings: store_link_js_1.StoreLinkSettingsDelivered.extend({
        exportOwnerPeriods: zod_1.z.array(store_link_js_1.ExportOwnerPeriod),
    }).optional(),
    /** RL-460 — imported cashiers and their store-system ids, for the export. */
    exportCashiers: zod_1.z.array(exports.LinkConfigExportCashier).optional(),
    generatedAt: zod_1.z.string().datetime(),
});
/**
 * Operator view of a Link device (Terminal Management → Link devices).
 * Carries connector sources as SUMMARIES only — no host, login or password.
 */
exports.LinkDeviceSourceSummary = zod_1.z.object({
    id: zod_1.z.string(),
    profile: exports.LinkConnectorProfile,
    name: zod_1.z.string(),
    enabled: zod_1.z.boolean(),
    /**
     * Back Office's import status for `ip-1`. A Link-only profile (`ip-2`) is
     * never imported by Back Office and reports `link`: its status is the
     * Link's to report (RL-453).
     */
    importStatus: zod_1.z.string(),
    lastSuccessfulImportAt: zod_1.z.string().datetime().nullable(),
    exportEnabled: zod_1.z.boolean(),
});
exports.LinkDeviceSummary = zod_1.z.object({
    id: zod_1.z.string(),
    code: zod_1.z.string(),
    displayName: zod_1.z.string(),
    status: zod_1.z.enum(['provisioned', 'active', 'revoked']),
    isOnline: zod_1.z.boolean(),
    lastHeartbeatAt: zod_1.z.string().datetime().nullable(),
    lastVersion: zod_1.z.string().nullable(),
    activatedAt: zod_1.z.string().datetime().nullable(),
    revokedAt: zod_1.z.string().datetime().nullable(),
    location: zod_1.z.object({ id: zod_1.z.string(), code: zod_1.z.string(), name: zod_1.z.string() }).nullable(),
    connectorSources: zod_1.z.array(exports.LinkDeviceSourceSummary),
});
exports.LinkDevicesResponse = zod_1.z.object({ devices: zod_1.z.array(exports.LinkDeviceSummary) });
// ------------------------------------------------------------------
// Link → Back Office: `POST /api/v1/link/status` (RL-453, ADR-0014 §7)
// ------------------------------------------------------------------
/**
 * What a Link tells Back Office about itself on every tick it is online: its
 * health and version, where tills reach it, the store-system import of each
 * connector source, and its export queue. Back Office keeps the latest report
 * per Link; the export-queue counts are what an `exportOwner` switch away from
 * `link` waits on (RL-452), and the operator view shows the rest.
 *
 * ------------------------------------------------------------------
 * GROWING IT
 * ------------------------------------------------------------------
 * The same rule as `/link/config`, in the other direction: later tickets add
 * OPTIONAL keys, and Back Office strips keys it does not know (never `strict`
 * here), so a newer Link is still accepted by an older Back Office — and a
 * field Back Office has not been taught about is never stored.
 *
 * ------------------------------------------------------------------
 * NO SECRETS
 * ------------------------------------------------------------------
 * Nothing in the report is a credential. The free-text fields (an import's
 * last error, an export's reason) are masked by the Link before sending and
 * scrubbed again by Back Office against the store's own passwords, capped in
 * length, and never logged. `advertisedUrl` must not carry user info.
 */
exports.LINK_STATUS_SCHEMA_VERSION = 1;
/** Longest free-text reason Back Office keeps; the Link truncates to it. */
exports.LINK_STATUS_TEXT_MAX = 500;
/** Failed / conflicting documents listed individually; the counts cover the rest. */
exports.LINK_STATUS_MAX_PROBLEMS = 50;
/** Retry requests handed to a Link per report. */
exports.LINK_STATUS_MAX_RETRY_REQUESTS = 50;
/**
 * - `ok` — paired, on a current configuration, its database answering, and
 *   no runner reporting a failure;
 * - `degraded` — working, but on a cached configuration, or a source import
 *   or an export is failing;
 * - `failing` — cannot do its job locally (its own database is down).
 */
exports.LinkHealth = zod_1.z.enum(['ok', 'degraded', 'failing']);
const StatusText = zod_1.z.string().max(exports.LINK_STATUS_TEXT_MAX);
const StatusId = zod_1.z.string().min(1).max(128);
/**
 * `sha256:` + 64 lowercase hex of the DER leaf — exactly the form the
 * till-facing `sync.store-link` carries and a till pins (RL-457), so a
 * reported fingerprint can be compared with a published one as a string.
 */
exports.LinkCertificateFingerprint = link_certificate_js_1.CertificateFingerprint;
/**
 * Where tills reach this Link. Only `http(s)`, and never with user info in it:
 * the URL is shown to operators and later published to tills (RL-457).
 */
exports.LinkAdvertisedUrl = zod_1.z
    .string()
    .max(512)
    .url()
    .refine((value) => {
    try {
        const url = new URL(value);
        return ((url.protocol === 'https:' || url.protocol === 'http:') &&
            url.username === '' &&
            url.password === '');
    }
    catch {
        return false;
    }
}, 'an http(s) URL without user info');
/**
 * - `not_running` — no import runner runs this source on the Link (none is
 *   registered, the source is disabled, or its profile is not in this build);
 * - `idle` / `running` / `ok` / `failed` — what the runner last did.
 */
exports.LinkSourceImportState = zod_1.z.enum(['not_running', 'idle', 'running', 'ok', 'failed']);
exports.LinkSourceStatus = zod_1.z.object({
    sourceId: StatusId,
    import: zod_1.z.object({
        state: exports.LinkSourceImportState,
        lastRunAt: zod_1.z.string().datetime().nullable(),
        lastSuccessAt: zod_1.z.string().datetime().nullable(),
        /** The import's position in the store system, as the runner names it. */
        cursor: zod_1.z.string().max(128).nullable(),
        /** Records the last run could not import. */
        faults: zod_1.z.number().int().min(0),
        lastError: StatusText.nullable(),
    }),
});
exports.LinkExportQueueCounts = zod_1.z.object({
    /** Waiting to be written, including failed ones scheduled to be tried again. */
    pending: zod_1.z.number().int().min(0),
    failed: zod_1.z.number().int().min(0),
    /** The number is held in the store system by a row this export did not write. */
    conflict: zod_1.z.number().int().min(0),
});
/** One document the Link could not export, with why. */
exports.LinkExportProblem = zod_1.z.object({
    /** The Link's own outbox id — what a retry request names. */
    id: StatusId,
    sourceId: StatusId,
    /** `receipt`, `cash_movement`, `opening_float`, `z_report`. */
    kind: zod_1.z.string().min(1).max(32),
    /**
     * The document id: Back Office's receipt, cash movement or shift id there;
     * on a Link, which never learns those, the till's idempotency key
     * `<terminalId>:<clientUuid>` (RL-460).
     */
    documentId: StatusId,
    status: zod_1.z.enum(['failed', 'conflict']),
    attempts: zod_1.z.number().int().min(0),
    reason: StatusText.nullable(),
    documentAt: zod_1.z.string().datetime().nullable(),
    lastAttemptAt: zod_1.z.string().datetime().nullable(),
});
/**
 * What the Link did with a retry request Back Office handed it:
 * - `queued` — put back in its outbox;
 * - `not_found` — no such document in its outbox;
 * - `not_retryable` — the document is not failed or conflicting (any more);
 * - `unsupported` — this Link runs no export.
 */
exports.LinkExportRetryOutcome = zod_1.z.enum([
    'queued',
    'not_found',
    'not_retryable',
    'unsupported',
]);
/**
 * RL-569 — how a Link's CRM loyalty connector (`ip-3`) is doing:
 * - `not_running` — no source, a disabled one, or not in this Link's build;
 * - `ok` — the last CRM call answered;
 * - `failing` — the last CRM call failed, and the breaker is still closed;
 * - `breaker_open` — calls fail at once ("unavailable") until the next probe.
 */
exports.LinkCrmState = zod_1.z.enum(['not_running', 'ok', 'failing', 'breaker_open']);
/**
 * Outcomes the Link counts per loyalty request. Classes, never values: the
 * identifier, the member and the CRM's text never reach the report.
 */
exports.LINK_CRM_OUTCOMES = [
    'found',
    'not_found',
    'blocked',
    'unavailable',
    'refused',
    'bad_request',
];
exports.LinkCrmOutcome = zod_1.z.enum(exports.LINK_CRM_OUTCOMES);
/**
 * A CRM error key as a stable code (`CannotFindCardNumber`, `timeout`, …).
 * Restricted to a code's alphabet so a CRM message — which can quote a phone
 * number — cannot be passed through it.
 */
exports.LinkCrmErrorKey = zod_1.z
    .string()
    .max(64)
    .regex(/^[A-Za-z][A-Za-z0-9_.-]*$/, 'a stable error code, not free text');
const LinkCrmOutcomeCounts = zod_1.z.object(Object.fromEntries(exports.LINK_CRM_OUTCOMES.map((outcome) => [outcome, zod_1.z.number().int().min(0)])));
/** Most tills listed in `crm.perTill`; a Link reports the busiest first. */
exports.LINK_CRM_MAX_TILLS = 200;
/**
 * RL-587: the Link's outbox of carded receipts for the CRM. Counts and an
 * age only — never a card, a receipt body or anything the CRM said.
 *
 * - `pending` — waiting to be sent (backing off after a failure included);
 * - `held` — kept, not sent, for a person: a return whose sale this Link
 *   never gave the CRM, or a write whose answer was lost and which the CRM
 *   does not show — never sent again on its own, so never counted twice;
 * - `rejected` — the CRM refused the receipt itself (card, product, body);
 * - `reconciled` — settled by asking the CRM for the purchase after a write
 *   whose answer was lost (all time).
 * - `offlinePending` — handed to the store's offline processing, but not yet
 *   confirmed in its table. This is separate from CRM `pending`.
 * - `offlineHeld` — handed off, but held for a person with no further Link
 *   writes; an earlier store write may have committed. The configured write
 *   destination/namespace differs from its pin, or the record predates the pin.
 *   Not counted in
 *   `offlinePending`.
 */
exports.LinkCrmReceiptQueue = zod_1.z.object({
    pending: zod_1.z.number().int().min(0),
    held: zod_1.z.number().int().min(0),
    rejected: zod_1.z.number().int().min(0),
    committedLastHour: zod_1.z.number().int().min(0),
    reconciled: zod_1.z.number().int().min(0),
    /** VL-103: optional for status reports from Links predating offline handoff. */
    offlinePending: zod_1.z.number().int().min(0).optional(),
    offlineHeld: zod_1.z.number().int().min(0).optional(),
    oldestOfflinePendingAt: zod_1.z.string().datetime().nullable().optional(),
    /** The oldest pending receipt's own time; null when nothing is pending. */
    oldestPendingAt: zod_1.z.string().datetime().nullable(),
    lastErrorKey: exports.LinkCrmErrorKey.nullable(),
    /**
     * RL-990: the held and rejected rows themselves, the oldest first, so an
     * operator can release or requeue one. Absent from a Link that predates it.
     */
    problems: zod_1.z.array(link_crm_receipt_actions_js_1.LinkCrmReceiptProblem).max(link_crm_receipt_actions_js_1.LINK_CRM_RECEIPT_PROBLEMS_MAX).optional(),
});
exports.LinkCrmStatus = zod_1.z.object({
    sourceId: StatusId,
    state: exports.LinkCrmState,
    lastSuccessAt: zod_1.z.string().datetime().nullable(),
    consecutiveFailures: zod_1.z.number().int().min(0),
    lastErrorKey: exports.LinkCrmErrorKey.nullable(),
    /** Requests per outcome over the last hour. */
    lastHour: LinkCrmOutcomeCounts,
    /**
     * Loyalty requests per till over the last hour, so an abnormal volume from
     * one pass (a lookup oracle) shows centrally. Terminal ids only.
     */
    perTill: zod_1.z
        .array(zod_1.z.object({ terminalId: StatusId, requests: zod_1.z.number().int().min(0) }))
        .max(exports.LINK_CRM_MAX_TILLS),
    /** RL-587: the receipt outbox. Absent from a Link that predates it, or that cannot read it. */
    receipts: exports.LinkCrmReceiptQueue.optional(),
});
/**
 * RL-936: what Back Office keeps of a report's `crm` block and shows the
 * operator — the connector's state and its receipt outbox: counts,
 * timestamps and stable keys. Not the per-till volumes or the call tallies,
 * and nothing the CRM said (`lastErrorKey` is a code, see [LinkCrmErrorKey]).
 * `receipts` is null for a Link whose connector runs without the outbox.
 */
exports.LinkCrmStatusView = exports.LinkCrmStatus.pick({
    sourceId: true,
    state: true,
    lastSuccessAt: true,
    lastErrorKey: true,
}).extend({ receipts: exports.LinkCrmReceiptQueue.nullable() });
/** SSC-228: no actor IDs, payloads or secrets; unavailable means counts unknown. */
exports.LinkSecureScaleStatus = zod_1.z.object({
    /** Last central replica attempt; a refusal is never described as a WAN outage. */
    upstreamState: zod_1.z.enum(['unknown', 'available', 'offline', 'refused', 'invalid']).optional(),
    upstreamHttpStatus: zod_1.z.number().int().min(400).max(499).optional(),
    storage: storage_pressure_js_1.SecureScaleStorageStatus.optional(),
    available: zod_1.z.boolean(),
    versionId: zod_1.z.string().max(128).nullable(),
    replicaReceivedAt: zod_1.z.string().datetime().nullable(),
    pendingBatches: zod_1.z.number().int().nonnegative(),
    detachedBatches: zod_1.z.number().int().nonnegative().optional(),
    rejectedBatches: zod_1.z.number().int().nonnegative(),
    oldestPendingAt: zod_1.z.string().datetime().nullable(),
});
/**
 * VL-353 — the document feed (`ip-4`), when the Link runs one. Absent from a
 * Link that does not, and from any Link built before it.
 *
 * `problems[].id` is the feed row's id — the id an operator retry names, on
 * the same retry route as the export queue's. The feed contributes nothing to
 * `exportQueue`, so it never holds an owner switch.
 */
exports.LinkDocumentFeedConnectionState = zod_1.z.enum([
    'disabled',
    'connecting',
    'connected',
    'down',
]);
exports.LinkDocumentFeedQueueStatus = zod_1.z.object({
    stopped: zod_1.z
        .object({
        reason: zod_1.z.enum(['legacy_lottery_rows', 'legacy_check_failed']),
        rows: zod_1.z.number().int().min(0).max(1000).nullable(),
    })
        .nullable()
        .optional(),
    key: zod_1.z.enum(exports.LINK_FEED_QUEUE_KEYS),
    enabled: zod_1.z.boolean(),
    pending: zod_1.z.number().int().min(0),
    failed: zod_1.z.number().int().min(0),
    blocked: zod_1.z.number().int().min(0),
    lastPublishedAt: zod_1.z.string().datetime().nullable(),
    lastPublishedNumber: zod_1.z.string().max(64).nullable(),
    oldestPendingAt: zod_1.z.string().datetime().nullable(),
});
exports.LinkDocumentFeedProblem = zod_1.z.object({
    id: StatusId,
    queue: zod_1.z.enum(exports.LINK_FEED_QUEUE_KEYS),
    documentId: StatusId,
    number: zod_1.z.string().max(64).nullable(),
    status: zod_1.z.enum(['failed', 'blocked']),
    attempts: zod_1.z.number().int().min(0),
    reason: StatusText.nullable(),
    documentAt: zod_1.z.string().datetime().nullable(),
    lastAttemptAt: zod_1.z.string().datetime().nullable(),
});
exports.LinkDocumentFeedStatus = zod_1.z.object({
    sourceId: StatusId,
    enabled: zod_1.z.boolean(),
    connection: zod_1.z.object({
        state: exports.LinkDocumentFeedConnectionState,
        since: zod_1.z.string().datetime().nullable(),
        lastError: StatusText.nullable(),
    }),
    /** One per queue key. A Back Office older than a queue refuses a report that lists it (VL-375). */
    queues: zod_1.z.array(exports.LinkDocumentFeedQueueStatus).max(exports.LINK_FEED_QUEUE_KEYS.length),
    problems: zod_1.z.array(exports.LinkDocumentFeedProblem).max(exports.LINK_STATUS_MAX_PROBLEMS),
});
exports.LinkStatusReport = zod_1.z.object({
    secureScale: exports.LinkSecureScaleStatus.nullable().optional(),
    schemaVersion: zod_1.z.literal(exports.LINK_STATUS_SCHEMA_VERSION),
    /** The Link's clock. Back Office judges freshness by when it RECEIVED the report. */
    generatedAt: zod_1.z.string().datetime(),
    health: exports.LinkHealth,
    version: zod_1.z.string().min(1).max(64),
    /** Null until the Link serves tills on the LAN (RL-457). */
    advertisedUrl: exports.LinkAdvertisedUrl.nullable(),
    /** Current first. Accepted and stored; their meaning belongs to RL-457. */
    certificateFingerprints: zod_1.z.array(exports.LinkCertificateFingerprint).max(4),
    sources: zod_1.z.array(exports.LinkSourceStatus).max(64),
    exportQueue: zod_1.z.object({
        /**
         * False when the Link could not read its own outbox this time. Back Office
         * then treats the queue as unknown — never as the zeros in `counts` — so
         * an owner switch keeps waiting.
         */
        available: zod_1.z.boolean().default(true),
        counts: exports.LinkExportQueueCounts,
        problems: zod_1.z.array(exports.LinkExportProblem).max(exports.LINK_STATUS_MAX_PROBLEMS),
    }),
    /** Outcomes of retry requests handed over in earlier responses. */
    retryResults: zod_1.z
        .array(zod_1.z.object({ requestId: StatusId, outcome: exports.LinkExportRetryOutcome }))
        .max(exports.LINK_STATUS_MAX_RETRY_REQUESTS * 2)
        .default([]),
    /**
     * RL-569 — the CRM loyalty connector, when the Link runs one. Absent from a
     * Link that does not, and from any Link built before it.
     */
    crm: exports.LinkCrmStatus.optional(),
    /**
     * VL-400 — what this Link build can do beyond the base report, e.g.
     * `export_correction_v1`. Absent from an older Link: it can do none of it,
     * and Back Office hands it nothing that needs one.
     */
    capabilities: zod_1.z.array(zod_1.z.string().min(1).max(64)).max(16).default([]),
    /**
     * VL-400 — correction results, observations and diagnostics (design §8.1).
     * Each element is parsed on its own by Back Office (§6.4); only the bound is
     * checked here, so one bad element never refuses the whole report.
     */
    correctionResults: zod_1.z.array(zod_1.z.unknown()).max(link_export_correction_js_1.LINK_CORRECTION_MAX_LINES).default([]),
    correctionObservations: zod_1.z.array(zod_1.z.unknown()).max(link_export_correction_js_1.LINK_CORRECTION_MAX_LINES).default([]),
    /** False when the Link could not read its correction or export tables. */
    correctionObservationsAvailable: zod_1.z.boolean().default(false),
    correctionDiagnostics: zod_1.z.array(zod_1.z.unknown()).max(link_export_correction_js_1.LINK_CORRECTION_MAX_LINES).default([]),
    /** Command elements without a valid request id, dropped this report (§7.1.3). */
    correctionDroppedElementCount: zod_1.z.number().int().min(0).default(0),
    /**
     * VL-378 — the gift-certificate mode block (`LinkGiftCertificateStatus` in
     * `link-gift-certificates.ts`). Kept opaque HERE on purpose: Back Office
     * parses it on its own, so a block it cannot read (a later mode, a variant
     * this build does not know) fails closed as "unreadable" instead of failing
     * the whole report. Absent from a Link built before it.
     */
    certificates: zod_1.z.unknown().optional(),
    /** VL-353 — the document feed (`ip-4`). Absent from a Link that runs none. */
    documentFeed: exports.LinkDocumentFeedStatus.optional(),
    /**
     * RL-847 — the card-provenance replay conflicts this Link recorded and Back
     * Office has not answered yet (`CardProvenanceConflictV1` in
     * `link-card-provenance.ts`). Only the bound is checked here; each element
     * is parsed on its own. Absent from a Link without `card_provenance_v1`,
     * and left absent (never defaulted) so an older report parses unchanged.
     */
    provenanceConflicts: zod_1.z.array(zod_1.z.unknown()).max(link_card_provenance_js_1.LINK_PROVENANCE_CONFLICT_MAX).optional(),
    /**
     * RL-990 — answers to CRM receipt actions handed over in earlier answers
     * (`LinkCrmReceiptActionResult`), parsed one by one by Back Office.
     */
    crmReceiptActionResults: zod_1.z
        .array(zod_1.z.unknown())
        .max(link_crm_receipt_actions_js_1.LINK_CRM_RECEIPT_ACTIONS_MAX * 2)
        .default([]),
});
/**
 * Back Office's answer: when it stored the report, and the retry requests an
 * operator has made for this Link's exports that the Link has not answered
 * yet. The Link answers each in the `retryResults` of a later report; one it
 * has already seen may be handed over again until it does.
 */
exports.LinkExportRetryRequest = zod_1.z.object({
    id: StatusId,
    exportId: StatusId,
    requestedAt: zod_1.z.string().datetime(),
});
exports.LinkStatusResponse = zod_1.z.object({
    receivedAt: zod_1.z.string().datetime(),
    retryRequests: zod_1.z.array(exports.LinkExportRetryRequest).max(exports.LINK_STATUS_MAX_RETRY_REQUESTS),
    /**
     * VL-400 — `export_correction_v1` commands and exact ACKs (design §6.1).
     * Only the bound is checked here; the Link parses each element on its own.
     * An older Link strips both keys (this object is not strict).
     */
    correctionCommands: zod_1.z.array(zod_1.z.unknown()).max(link_export_correction_js_1.LINK_CORRECTION_MAX_COMMANDS).default([]),
    correctionAcks: zod_1.z.array(zod_1.z.unknown()).max(link_export_correction_js_1.LINK_CORRECTION_MAX_ACKS).default([]),
    /**
     * VL-378 — certificate enablement requests (`LinkGiftCertificateEnablementRequest`)
     * and certificate releases in either protocol's shape. Opaque here, read
     * item by item by the Link, so one unreadable entry cannot fail the answer
     * that carries the export retries. A Link of the disabled build answers
     * every enablement `unsupported_by_build` and every release
     * `release_disabled`, and changes nothing else. Absent from an older Back
     * Office; ignored by an older Link.
     */
    certificateEnablementRequests: zod_1.z.unknown().optional(),
    certificateReleaseRequests: zod_1.z.unknown().optional(),
    certificateReleaseCommands: zod_1.z.unknown().optional(),
    /**
     * RL-847 — one `CardProvenanceConflictAck` per conflict element of the
     * report. Only the bound is checked here; the Link parses each on its own.
     * Absent from an older Back Office, and ignored by an older Link.
     */
    provenanceConflictAcks: zod_1.z.array(zod_1.z.unknown()).max(link_card_provenance_js_1.LINK_PROVENANCE_CONFLICT_MAX).optional(),
    /**
     * RL-990 — operator releases and requeues of CRM receipts
     * (`LinkCrmReceiptAction`), only for a Link that advertises
     * `crm_receipt_actions_v1`; parsed one by one by the Link, which answers
     * each in `crmReceiptActionResults`.
     */
    crmReceiptActions: zod_1.z.array(zod_1.z.unknown()).max(link_crm_receipt_actions_js_1.LINK_CRM_RECEIPT_ACTIONS_MAX).default([]),
});
// ------------------------------------------------------------------
// Operator view: `GET /api/v1/connectors/locations/:locationId/link-status`
// ------------------------------------------------------------------
/**
 * How the operator view presents a Link's last report:
 * - `no_link` — the store has no Link that is not revoked;
 * - `missing` — it has one, and it has never reported;
 * - `stale` — its last report is older than `LINK_STATUS_STALE_AFTER_MS`, so
 *   nothing in it is presented as current;
 * - otherwise the health the Link reported.
 */
exports.LinkReportState = zod_1.z.enum(['no_link', 'missing', 'stale', 'ok', 'degraded', 'failing']);
/** A report older than this is stale — the same age the owner-switch guard accepts. */
exports.LINK_STATUS_STALE_AFTER_MS = 10 * 60_000;
exports.LinkExportRetryRequestView = zod_1.z.object({
    id: zod_1.z.string(),
    exportId: zod_1.z.string(),
    requestedAt: zod_1.z.string().datetime(),
    requestedBy: zod_1.z.string().nullable(),
    /** Last time Back Office handed it to the Link; null = not collected yet. */
    deliveredAt: zod_1.z.string().datetime().nullable(),
    completedAt: zod_1.z.string().datetime().nullable(),
    outcome: exports.LinkExportRetryOutcome.nullable(),
});
/**
 * RL-847 §3.2 — what Back Office knows of whether the store's current Link
 * device, in its current activation, accepts card provenance
 * (`card_provenance_v1`). The state is monotonic and fenced: report age never
 * changes it, and only a positive newer than every report already applied
 * (on both clocks) restores `ready`.
 *
 * `regressed` is the operator alert: the Link was `ready` and a later report
 * no longer lists the capability — "the Link no longer accepts card
 * provenance; tills hold documents for it". It stays while the state is
 * `not_ready`. The remedy is to update the Link.
 */
exports.LinkCardProvenanceSupportView = zod_1.z
    .object({
    state: zod_1.z.enum(['ready', 'not_ready', 'unknown']),
    /** +1 on every transition; 0 when there is no state yet. */
    stateVersion: zod_1.z.number().int().nonnegative(),
    regressed: zod_1.z.boolean(),
    /** When the state last changed; null when there is no state yet. */
    changedAt: zod_1.z.string().datetime().nullable(),
})
    .strict();
exports.LinkStatusView = zod_1.z.object({
    locationId: zod_1.z.string(),
    exportOwner: zod_1.z.enum(['off', 'backoffice', 'link']),
    state: exports.LinkReportState,
    device: zod_1.z
        .object({
        id: zod_1.z.string(),
        code: zod_1.z.string(),
        displayName: zod_1.z.string(),
        status: zod_1.z.enum(['provisioned', 'active', 'revoked']),
        lastHeartbeatAt: zod_1.z.string().datetime().nullable(),
    })
        .nullable(),
    /** The last report as received, whatever its age; null = never. */
    report: zod_1.z
        .object({
        secureScale: exports.LinkSecureScaleStatus.nullable().optional(),
        receivedAt: zod_1.z.string().datetime(),
        generatedAt: zod_1.z.string().datetime(),
        health: exports.LinkHealth,
        version: zod_1.z.string(),
        advertisedUrl: zod_1.z.string().nullable(),
        certificateFingerprints: zod_1.z.array(zod_1.z.string()),
        sources: zod_1.z.array(exports.LinkSourceStatus.extend({ name: zod_1.z.string().nullable() })),
    })
        .nullable(),
    /**
     * The export queue as the owner-switch guard sees it: null unless a report
     * is fresh (and since the last owner change), so an old count is never
     * shown as the current one.
     */
    exportQueue: zod_1.z
        .object({
        counts: exports.LinkExportQueueCounts,
        problems: zod_1.z.array(exports.LinkExportProblem),
        reportedAt: zod_1.z.string().datetime(),
    })
        .nullable(),
    retryRequests: zod_1.z.array(exports.LinkExportRetryRequestView),
    /**
     * RL-554: what the Link relayed to Back Office per source — cashiers and
     * promotions of a store whose catalogue it owns. Empty when it relays
     * nothing.
     */
    relay: zod_1.z.array(link_relay_js_1.LinkRelaySourceView).default([]),
    /**
     * VL-400 — whether the store's live Link advertised `export_correction_v1`
     * in a fresh report. A hint for the screen only; approval re-checks it.
     */
    correctionSupported: zod_1.z.boolean().default(false),
    /** VL-400 — correction requests for this store's live Link, newest first. */
    corrections: zod_1.z.array(link_export_correction_js_1.LinkExportCorrectionView).default([]),
    /**
     * VL-378 — what the store's Link last reported about gift certificates;
     * null when it never reported a readable block. Always `disabled` in this
     * build.
     */
    giftCertificates: link_gift_certificates_js_1.LinkGiftCertificateStatusView.nullable().default(null),
    /**
     * VL-353 — the document feed as the Link last reported it, under the same
     * freshness rule as `exportQueue`: null unless a report is fresh and
     * carried the block. Optional so a client reading an older Back Office's
     * answer sees it as absent.
     */
    documentFeed: exports.LinkDocumentFeedStatus.extend({ reportedAt: zod_1.z.string().datetime() })
        .nullable()
        .optional(),
    /**
     * RL-936 — the CRM loyalty connector and its receipt outbox as the Link last
     * reported them, under the same freshness rule as `documentFeed`: null
     * unless a report is fresh and carried the block. Optional so a client
     * reading an older Back Office's answer sees it as absent.
     */
    crm: exports.LinkCrmStatusView.extend({ reportedAt: zod_1.z.string().datetime() }).nullable().optional(),
    /**
     * RL-990 — whether the store's live Link advertised `crm_receipt_actions_v1`
     * in a fresh report. A hint for the screen; a request re-checks it.
     */
    crmReceiptActionsSupported: zod_1.z.boolean().default(false),
    /** RL-990 — operator actions on this store's live Link's CRM receipts, newest first. */
    crmReceiptActions: zod_1.z.array(link_crm_receipt_actions_js_1.LinkCrmReceiptActionView).default([]),
    /**
     * RL-847 §3.2 — the card-provenance support state of the store's live Link,
     * with the regression alert. Optional so a client reading an older Back
     * Office's answer sees it as absent.
     */
    cardProvenance: exports.LinkCardProvenanceSupportView.optional(),
});
//# sourceMappingURL=link.js.map